// The Welcome → Menu tape-transport score.
//
// Concept (decided 2026-07-29, see docs/motion-audit-first-screens.md): the
// cassette from the splash returns. Spools spin up, the tape tensions, the
// screen shuttles horizontally, and the menu is what's on the other side.
//
// Beat order:
//   spinUp   spools engage, cassette seats into frame
//   tension  Welcome content is pulled toward the tape path
//   cover    tape bands close to full opacity
//   COMMIT   ← navigation fires here, hidden behind the cover
//   scrub    horizontal shuttle at speed
//   thread   the title travels from Welcome's h1 to Menu's header wordmark
//   settle   cover retracts, spools decelerate, Menu threads out
//
// The generic machinery (interpolation, scaling, the cover/commit contract)
// lives in `sequence.ts`; this file is only the performance.

import type { MotionTier } from './motion';
import { type Beat, type SequenceScore, scaleSequence, devSpeedFactor } from './sequence';

export interface TapeTimeline extends SequenceScore {
  spinUp: Beat;
  tension: Beat;
  scrub: Beat;
  thread: Beat;

  // --- Decorative budget, tier-scaled -------------------------------------
  /** Horizontal wow-and-flutter streaks drawn during the scrub. */
  streakCount: number;
  /** Rotations per second the spools reach at full transport speed. */
  spoolTurns: number;
}

const FULL: TapeTimeline = {
  tier: 'full',
  spinUp: { at: 0.0, dur: 0.34 },
  tension: { at: 0.2, dur: 0.28 },
  cover: { at: 0.3, dur: 0.2 },
  commitAt: 0.56,
  scrub: { at: 0.5, dur: 0.58 },
  thread: { at: 0.66, dur: 0.56 },
  settle: { at: 1.08, dur: 0.44 },
  total: 1.52,
  streakCount: 14,
  spoolTurns: 3,
  travels: true,
};

/**
 * Same choreography, compressed, decoration trimmed. Not a user preference —
 * this is what a 2GB / 4-core Android gets so the shuttle still hits 60fps.
 */
const LOW_POWER: TapeTimeline = {
  tier: 'lowPower',
  spinUp: { at: 0.0, dur: 0.24 },
  tension: { at: 0.14, dur: 0.2 },
  cover: { at: 0.22, dur: 0.16 },
  commitAt: 0.42,
  scrub: { at: 0.38, dur: 0.4 },
  thread: { at: 0.48, dur: 0.4 },
  settle: { at: 0.78, dur: 0.32 },
  total: 1.1,
  streakCount: 5,
  spoolTurns: 2,
  travels: true,
};

/**
 * The authored reduced-motion variant. Deliberately NOT a cut: the cassette
 * still appears, the tape still covers, the wordmark still resolves — it just
 * does all of it by dissolving rather than travelling or spinning.
 */
const REDUCED: TapeTimeline = {
  tier: 'reduced',
  spinUp: { at: 0.0, dur: 0.14 },
  tension: { at: 0.0, dur: 0.0 },
  cover: { at: 0.1, dur: 0.16 },
  commitAt: 0.3,
  scrub: { at: 0.26, dur: 0.0 },
  thread: { at: 0.26, dur: 0.18 },
  settle: { at: 0.34, dur: 0.24 },
  total: 0.58,
  streakCount: 0,
  spoolTurns: 0,
  travels: false,
};

export function buildTapeTimeline(tier: MotionTier): TapeTimeline {
  const base = tier === 'reduced' ? REDUCED : tier === 'lowPower' ? LOW_POWER : FULL;
  return scaleSequence(base, devSpeedFactor());
}

// Re-exported so existing consumers and tests keep a single import site for
// "everything about the tape sequence".
export {
  beatEnd,
  coverOpaqueWindow,
  sampleTrack,
  scaleSequence,
  scaleSequence as scaleTimeline,
  ALL_TIERS,
  type Beat,
  type Stop,
  type SequenceScore,
} from './sequence';
